import React, { useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { FuelTextField, FuelLoading } from '@frete.com/fuel-react';
import { formatCEP, regexOnlyNumbers } from '@utils/formatters';
import { isValidZipCode } from '@utils/validate';
import { getAddressWithZipCode } from '@services/cep';
import { ZipCodeInterface } from '@interface/cep';
import { EditAddressType } from '../schema';
import { FieldZipCodePropsInterface } from './FieldZipCodeTypes';

function FieldZipCode({ flow }: FieldZipCodePropsInterface) {
  const { clearErrors, control, register, setError, setValue, formState } =
    useFormContext<EditAddressType>();
  const [isLoading, setIsLoading] = useState(false);
  const [valueState, setValueState] = useState('');
  const nameField = `${flow}.place.zipCode` as const;
  const errorField = formState.errors?.[flow]?.place?.zipCode;
  const errorMessage = errorField?.message;
  const sizeZipCodeOnlyNumbers = 8;

  function clearAddress() {
    setValue(`${flow}.place.city.name`, '');
    setValue(`${flow}.place.state.acronym`, '');
    setValue(`${flow}.place.state.name`, '');
    setValue(`${flow}.place.address`, '');
  }

  function fillFrom(data: ZipCodeInterface) {
    if (data.localidade) {
      const fieldCityName = `${flow}.place.city.name` as const;

      setValue(fieldCityName, data.localidade);
      clearErrors(fieldCityName);
    }

    if (data.uf) {
      const fieldStateAcronym = `${flow}.place.state.acronym` as const;

      setValue(fieldStateAcronym, data.uf);
      clearErrors(fieldStateAcronym);
    }

    if (data.estado) {
      const fieldStateName = `${flow}.place.state.name` as const;

      setValue(fieldStateName, data.estado);
      clearErrors(fieldStateName);
    }

    if (data.logradouro) {
      const fieldAddressName = `${flow}.place.address` as const;
      let addressText = data.logradouro;

      if (data.bairro) {
        addressText += ` - ${data.bairro}`;
      }

      setValue(fieldAddressName, addressText);
      clearErrors(fieldAddressName);
    }
  }

  function getOnlyNumbers(text: string) {
    return text.replace(regexOnlyNumbers, '');
  }

  function handleBlur(onBlur: () => void): void {
    /*
      Caso o erro seja custom (viaCep), não chama o
      onBlur, para não remove-lo
    */
    if (errorField?.type === 'custom') {
      return;
    }

    onBlur();
  }

  function handleChange(e: {
    detail: {
      component: 'fuel-text-field';
      value: string;
    };
  }): string {
    const { value } = e.detail;
    const CEP = formatCEP(value.replace(regexOnlyNumbers, ''));

    setValueState(CEP);

    return CEP;
  }

  useEffect(() => {
    if (valueState) {
      const zipCodeOnlyNumbers = getOnlyNumbers(valueState);
      clearErrors(nameField);

      if (isValidZipCode(zipCodeOnlyNumbers)) {
        clearAddress();
        // setTimeout utilizado para o usuário conseguir visualizar aos dados do form sendo alterado
        setIsLoading(true);
        setTimeout(() => {
          getAddressWithZipCode(zipCodeOnlyNumbers)
            .then((response) => {
              if (!response.erro) {
                fillFrom(response);
                return;
              }

              setError(nameField, {
                type: 'custom',
                message:
                  'Não encontramos o CEP. Preencha o endereço nos campos.',
              });
            })
            .catch(() => {
              setError(nameField, {
                type: 'custom',
                message:
                  'Não encontramos o CEP. Preencha o endereço nos campos.',
              });
            })
            .finally(() => {
              setIsLoading(false);
            });
        }, 400);

        return;
      }

      if (zipCodeOnlyNumbers.length === sizeZipCodeOnlyNumbers) {
        setError(nameField, {
          type: 'custom',
          message: 'Digite um CEP válido.',
        });
      }
    }
  }, [valueState]);

  return (
    <Controller
      name={nameField}
      control={control}
      render={({ field: { value, onBlur, onChange } }) => (
        <FuelTextField
          label="CEP"
          name={nameField}
          data-testid="input-zipCode"
          onFuel-blur={() => {
            handleBlur(onBlur);
          }}
          onFuel-input={(e) => onChange(handleChange(e))}
          placeholder="00000-000"
          ref={register(nameField).ref}
          value={value}
          maxlength={9}
          state={errorMessage ? 'error' : 'default'}
          hint={errorMessage || ''}
          controlled
          hasSuffix={isLoading}
        >
          {isLoading && <FuelLoading slot="suffix" />}
        </FuelTextField>
      )}
      defaultValue=""
    />
  );
}

export default FieldZipCode;
